import {OperationTypes} from '@jimmyn/apollo-client';
import {Button, Modal} from 'antd';
import {
  CapturePaymentInput,
  ChargeFieldsFragment,
  Maybe,
  useCapturePaymentMutation
} from 'api/generated';
import {chargeQuery} from 'api/queries';
import {AnchorLink} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, InputNumber} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import React, {useState} from 'react';
import * as yup from 'yup';

type Props = {payment?: Maybe<ChargeFieldsFragment>};

export const CapturePayment: React.FC<Props> = ({payment}) => {
  const [isOpen, setOpen] = useState(false);
  const [capturePayment] = useCapturePaymentMutation({
    updateQuery: {query: chargeQuery, variables: {id: payment?.id}},
    operationType: OperationTypes.UPDATE
  });

  if (!payment) return null;

  const amount = (payment.amount || 0) / 100;

  const validationSchema = yup.object().shape({
    amount: yup.number().required().moreThan(0).max(amount)
  });

  const initialValues: CapturePaymentInput = {
    accountId: payment.accountId,
    amount: 0,
    paymentId: payment.id
  };

  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: payment?.currency || 'EUR'
  });

  const formattedAmount = currency.format(amount);

  const handleCapture = async (
    {amount, paymentId}: CapturePaymentInput,
    {setSubmitting, resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    await capturePayment({
      variables: {
        input: {
          accountId: payment.accountId,
          paymentId,
          amount: Math.floor(amount! * 100)
        }
      }
    });
    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>Capture</Button>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleCapture}>
        {({submitForm, isSubmitting, values, errors, resetForm, setFieldValue}) => (
          <Modal
            title="Capture payment"
            okText={'Capture ' + currency.format(values.amount!)}
            cancelText="Cancel"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            onCancel={() => {
              setOpen(false);
              setTimeout(resetForm, 100);
            }}>
            <Form layout="vertical">
              <FormItem
                name="amount"
                label="Amount"
                {...(errors.amount ? {} : {help: null})}
                extra={
                  <>
                    {formattedAmount} available for capture.{' '}
                    <AnchorLink onClick={() => setFieldValue('amount', amount)}>
                      Capture all
                    </AnchorLink>
                    .
                  </>
                }>
                <InputNumber
                  type="number"
                  name="amount"
                  style={{width: '100%'}}
                  precision={2}
                  addonAfter={payment.currency}
                />
              </FormItem>
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
